/**
 * The People request form — one form for all three queues.
 *
 * Shape (David's review, Oct 1 2026): the three teams are collapsible sections (the design-system
 * Accordion, one open at a time); opening one shows its categories as small boxes carrying only
 * the category name. Picking a category is what chooses the queue. Then only the questions that
 * category needs, then the description. There is NO title field — the proxy titles the ticket
 * from the description (`autoSummary` in proxy-server/lib/peopleTickets.js).
 *
 * One radius for every control on the form (`rounded`, 4px — the design-system atoms are 2–4px)
 * and the design-system type and colour tokens (`body-*`, `text-iron`, `border-pearl`), so the
 * form reads like the rest of People Space rather than like generic Tailwind.
 *
 * It never asks for a department, an assignee or a status. The proxy stamps the department and
 * routes the ticket through Ticket Routing.
 */
import { useMemo, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@finn-auto/design-system/atoms/accordion';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@finn-auto/design-system/atoms/select';
import { cn } from '../../../../shared';
import { readFileAsBase64, submitPeopleTicket, TicketRequestError } from '../../services/peopleTicketsApi';
import type { PeopleTicketOptions, SentReceipt, TicketPriority, TicketQueue } from '../../types/peopleTickets';
import { QUEUE_TITLES, SUBCATEGORY_LABEL, URGENCY_HINT, descriptionLabel, optionLabel } from './requestCopy';

type Errors = Record<string, string>;

const RADIUS = 'rounded';
const MB = 1024 * 1024;
/** Up to this many sub-options are shown as boxes; more (the 15 benefits) as a dropdown. */
const MAX_SUBOPTION_BOXES = 4;

/** A row of small option boxes. Radio semantics on plain buttons: these are choices, not actions. */
function OptionBoxes<T extends string>({
  label,
  options,
  value,
  onChange,
  render = optionLabel,
  size = 'answer',
  disabled,
}: {
  label: string;
  options: readonly T[];
  value: T | '';
  onChange: (next: T) => void;
  render?: (option: T) => string;
  /** 'subtitle' for the section categories (David, Oct 1 2026): at answer size they read huge. */
  size?: 'answer' | 'subtitle';
  disabled: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((option) => {
        const active = value === option;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(option)}
            className={cn(
              RADIUS,
              // before/after:hidden — the app's global button styles add a pseudo-element layer
              // that covered each box's top border (the design-system trigger turns it off too).
              'border border-solid transition-colors before:hidden after:hidden',
              size === 'subtitle' ? 'px-3 py-2 body-14-regular' : 'px-4 py-2.5 body-16-regular',
              'disabled:cursor-not-allowed disabled:opacity-50',
              active ? 'border-trustedBlue bg-trustedBlue text-white' : 'border-pearl bg-white text-black hover:bg-cotton',
            )}
          >
            {render(option)}
          </button>
        );
      })}
    </div>
  );
}

function Question({ title, hint, error, children }: { title: string; hint?: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      {/* Generous on purpose: the design-system type classes trim their own line boxes, so the
          visible gap is smaller than the margin. */}
      <p className={cn(hint ? 'mb-2.5' : 'mb-3', 'body-16-semibold text-black')}>{title}</p>
      {hint && <p className="mb-4 body-14-regular text-iron">{hint}</p>}
      {children}
      {error && <p className="mt-2 text-red body-14-regular">{error}</p>}
    </div>
  );
}

export function RequestForm({
  options,
  onSubmitted,
  submit = submitPeopleTicket,
  initialDescription = '',
}: {
  options: PeopleTicketOptions;
  onSubmitted: (receipt: SentReceipt) => void;
  /** Defaults to the real proxy call; injectable so the form can be rendered without one. */
  submit?: typeof submitPeopleTicket;
  /** Pre-filled description — what someone asked Chat with Boris when it could not answer. */
  initialDescription?: string;
}) {
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [queue, setQueue] = useState<TicketQueue | ''>('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [description, setDescription] = useState(() =>
    initialDescription.slice(0, options.limits.description)
  );
  const [location, setLocation] = useState('');
  const [restockReason, setRestockReason] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('Regular');
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const subcategories = category ? options.categorySubcategories[category] ?? [] : [];
  const { limits } = options;

  /** Picking a category is what chooses the queue; a subcategory only survives inside its category. */
  function pick(nextQueue: TicketQueue, nextCategory: string) {
    setQueue(nextQueue);
    setCategory(nextCategory);
    if (!options.categorySubcategories[nextCategory]?.includes(subcategory)) setSubcategory('');
    setErrors(({ queue: _q, category: _c, ...rest }) => rest);
  }

  function addFiles(list: FileList | null) {
    if (!list) return;
    const next = [...files, ...Array.from(list)].slice(0, limits.attachments);
    const tooBig = next.find((f) => f.size > limits.attachmentBytes);
    const total = next.reduce((sum, f) => sum + f.size, 0);
    if (tooBig) return setErrors((e) => ({ ...e, attachments: `${tooBig.name} is larger than ${limits.attachmentBytes / MB} MB` }));
    if (total > limits.attachmentTotalBytes) {
      return setErrors((e) => ({ ...e, attachments: `Attachments add up to more than ${limits.attachmentTotalBytes / MB} MB` }));
    }
    setErrors(({ attachments: _gone, ...rest }) => rest);
    setFiles(next);
  }

  /** Emptiness only — the proxy owns the real rules and answers with per-field messages. */
  const missing = useMemo(() => {
    const m: Errors = {};
    if (!category) m.category = 'Pick what your request is about';
    if (subcategories.length && !subcategory) m.subcategory = 'Pick one of the options';
    if (!description.trim() && category !== 'Keycard') m.description = 'Describe what you need';
    if (category === 'Repair / Damage' && !location.trim()) m.location = 'Where is it?';
    if (category === 'Restock' && !restockReason) m.restockReason = 'Why is this needed?';
    return m;
  }, [category, subcategories.length, subcategory, description, location, restockReason]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Guarded here as well as by `disabled`: Enter in a field is an implicit submit, and a second
    // press while the first request is in flight would file the same request twice.
    if (submitting) return;
    if (Object.keys(missing).length) return setErrors(missing);
    setSubmitting(true);
    setFormError('');
    try {
      const attachments = await Promise.all(
        files.map(async (f) => ({
          filename: f.name,
          contentType: f.type || 'application/octet-stream',
          data: await readFileAsBase64(f),
        })),
      );
      const { ticket, attachmentsFailed } = await submit({
        queue: queue as TicketQueue,
        category,
        subcategory: subcategory || undefined,
        description: description.trim(),
        priority,
        location: category === 'Repair / Damage' ? location.trim() : undefined,
        restockReason: category === 'Restock' ? restockReason : undefined,
        attachments,
      });
      onSubmitted({ ticket, attachmentsSent: files.length, attachmentsFailed });
    } catch (err) {
      if (err instanceof TicketRequestError && Object.keys(err.details).length) setErrors(err.details);
      setFormError(err instanceof Error ? err.message : 'Your request could not be sent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-8">
      <Question title="What is your request about?" error={errors.category || errors.queue}>
        {/* type="multiple": several sections may be open at once (David, Oct 1 2026). */}
        <Accordion type="multiple" value={openSections} onValueChange={setOpenSections} className="space-y-2">
          {options.queues.map((q) => {
            const chosenHere = queue === q && Boolean(category);
            const title = QUEUE_TITLES[q] ?? q;
            return (
              <AccordionItem
                key={q}
                value={q}
                className={cn(RADIUS, 'border border-solid px-4', chosenHere ? 'border-trustedBlue' : 'border-pearl')}
              >
                <AccordionTrigger withBorder={false} className="py-4" disabled={submitting}>
                  <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 pr-3">
                    <span className="body-16-semibold text-black">{title}</span>
                    {/* The pick, in blue after the title, once the section is folded away. */}
                    {chosenHere && !openSections.includes(q) && (
                      <span className="body-16-regular text-trustedBlue">{category}</span>
                    )}
                  </span>
                </AccordionTrigger>
                {/* alwaysMounted=false: a collapsed section's boxes would otherwise stay in the
                    tab order and be reachable by keyboard while invisible. border-b-0: the
                    content's own divider drew a stray line inside the box.
                    The top row's borders were hidden by TWO things, both on the content wrapper:
                    its design-system type class (body-16-light) trims its top with a negative-
                    margin ::before, which pulled the boxes ~5px up under the header — hence
                    before/after:hidden; and it animates to a height it measures once (90px for
                    100px of boxes) inside overflow:hidden — hence overflow-visible and no
                    animation. A closed section's content is unmounted, so nothing needs clipping. */}
                <AccordionContent
                  alwaysMounted={false}
                  wrapperClassName="border-b-0 overflow-visible before:hidden after:hidden data-[state=open]:animate-none data-[state=closed]:animate-none"
                  className="pb-4 pt-1"
                >
                  <OptionBoxes
                    label={`${title} categories`}
                    options={options.queueCategories[q] ?? []}
                    value={queue === q ? category : ''}
                    onChange={(c) => pick(q, c)}
                    size="subtitle"
                    disabled={submitting}
                  />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </Question>

      {subcategories.length > 0 && (
        <Question title={SUBCATEGORY_LABEL[category] ?? 'Details'} error={errors.subcategory}>
          {subcategories.length <= MAX_SUBOPTION_BOXES ? (
            <OptionBoxes
              label={SUBCATEGORY_LABEL[category] ?? 'Details'}
              options={subcategories}
              value={subcategory}
              onChange={setSubcategory}
              disabled={submitting}
            />
          ) : (
            <Select value={subcategory} onValueChange={setSubcategory} disabled={submitting}>
              <SelectTrigger aria-label={SUBCATEGORY_LABEL[category] ?? 'Details'} className={cn(RADIUS, 'w-full')}>
                <SelectValue placeholder="Choose one" />
              </SelectTrigger>
              <SelectContent>
                {subcategories.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Question>
      )}

      {category === 'Repair / Damage' && (
        <Question title="Where did you find the damage?" error={errors.location}>
          <Input
            aria-label="Where did you find the damage?"
            value={location}
            placeholder="e.g. Kitchen, 2nd floor"
            maxLength={limits.location}
            disabled={submitting}
            className={RADIUS}
            onChange={(e) => setLocation(e.target.value)}
          />
        </Question>
      )}

      {category === 'Restock' && (
        <Question title="Why is it needed?" error={errors.restockReason}>
          <OptionBoxes label="Why is it needed?" options={options.restockReasons} value={restockReason} onChange={setRestockReason} disabled={submitting} />
        </Question>
      )}

      {category && (
        <>
          <Question title={descriptionLabel(category)} error={errors.description}>
            <Textarea
              aria-label={descriptionLabel(category)}
              rows={5}
              value={description}
              placeholder={descriptionLabel(category) === 'Describe your request' ? 'What do you need, and by when?' : undefined}
              maxLength={limits.description}
              disabled={submitting}
              className={RADIUS}
              textAreaClassName="body-14-regular"
              onChange={(e) => setDescription(e.target.value)}
            />
          </Question>

          <Question title="How urgent is it?" hint={URGENCY_HINT}>
            <OptionBoxes label="How urgent is it?" options={options.priorities} value={priority} onChange={setPriority} disabled={submitting} />
          </Question>

          <Question
            title="Attachments (optional)"
            hint={`Up to ${limits.attachments} files, ${limits.attachmentBytes / MB} MB each`}
            error={errors.attachments}
          >
            {files.length > 0 && (
              <ul className="mb-2 space-y-1">
                {files.map((f) => (
                  <li key={`${f.name}-${f.size}`} className="flex items-center gap-2 body-16-regular text-black before:hidden after:hidden">
                    <Paperclip className="h-4 w-4 text-iron" />
                    <span className="min-w-0 truncate">{f.name}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${f.name}`}
                      disabled={submitting}
                      onClick={() => setFiles((prev) => prev.filter((x) => x !== f))}
                      className="text-iron hover:text-black"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {files.length < limits.attachments && (
              <label
                className={cn(
                  RADIUS,
                  'inline-flex cursor-pointer items-center gap-2 border border-dashed border-pearl px-4 py-2.5 body-16-regular text-black hover:bg-cotton',
                  // A design-system type class on a flex container adds its trimming ::before as an
                  // empty first flex item, which the gap turns into an offset before the icon.
                  'before:hidden after:hidden',
                )}
              >
                <Paperclip className="h-4 w-4" />
                Add a file
                <input
                  type="file"
                  multiple
                  className="sr-only"
                  disabled={submitting}
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </Question>
        </>
      )}

      {formError && <p className={cn(RADIUS, 'bg-red/10 px-4 py-3 text-red body-16-regular')}>{formError}</p>}

      <Button type="submit" loading={submitting} disabled={submitting || !category} className={RADIUS}>
        Send request
      </Button>
    </form>
  );
}
